import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Button } from '@avangard/ui/core';
import { breakpoints } from '@avangard/ui/utils';

import { ArrayHelper } from '@helpers';
import { useEnqueueStacks, useLocationQuery } from '@modules/layout/hooks';
import { Loader } from '@modules/layout/moleculas';
import { useTestingQuestion } from '@modules/testing/hooks';
import {
    FinishTestButton,
    TestingAnswers,
    TestingNavigation,
    TestingQuestion,
} from '@modules/testing/moleculas';
import { TestingTimer } from '@modules/testing/organisms';
import { AnswerToQuestion } from '@modules/testing/graphql';

import type { TestEntity } from '@modules/testing/entities';
import {
    AnswerToQuestionMutationType,
    AnswerToQuestionMutationVariables,
} from '@modules/types/graphql';
import { useEventAsync } from '@modules/shared/hooks';

type TestingFormProps = {
    test: TestEntity;
    onAnswer: () => void | Promise<void>;
    onTimeUp: () => void | Promise<void>;
    onFinish: () => void | Promise<void>;
};

const Root = styled.div`
    width: 83.3%;
    height: 100%;

    ${breakpoints.down('sm')} {
        width: 100%;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin: 30px 0 20px 0;

    ${breakpoints.down('sm')} {
        margin: 16px 0;
    }

    ${breakpoints.down('xs')} {
        flex-wrap: wrap;
        margin: 0 0 16px;

        & > *:last-child {
            order: -1;
            margin-bottom: 20px;
            width: 100%;
        }
    }
`;

const TestingForm = (props: TestingFormProps): React.ReactElement => {
    const { test, onAnswer, onTimeUp, onFinish } = props;

    const { t } = useTranslation(['testing']);

    const { enqueueError } = useEnqueueStacks();

    const { query, setQuery } = useLocationQuery();
    const currentQuestionIndex = query.question ? Number(query.question) : 1;
    const currentQuestionId = test.questionIds[currentQuestionIndex - 1];

    const [answerIds, setAnswerIds] = React.useState<string[]>(() => []);
    const [serverUserAnswerIds, setServerUserAnswerIds] = React.useState<string[] | null>(null);

    const { testingQuestion, loading: testingQuestionLoading } = useTestingQuestion({
        skip: !currentQuestionId,
        variables: { id: currentQuestionId as string },
    });

    const [sendAnswer, { loading: sendAnswerLoading }] = useMutation<
        AnswerToQuestionMutationType,
        AnswerToQuestionMutationVariables
    >(AnswerToQuestion);

    React.useEffect(() => {
        if (!currentQuestionId) {
            setQuery({ ...query, question: undefined });
        }
    }, [currentQuestionId]);

    React.useEffect(() => {
        const getQuestionAnswersFromTest = (questionId: string): string[] | null => {
            const currentAnswers = test.answers.find(answer => answer.questionId === questionId);

            if (currentAnswers) {
                return currentAnswers.answerIds.filter(Boolean) as string[];
            }

            return null;
        };

        const answers = getQuestionAnswersFromTest(testingQuestion.id);

        setAnswerIds(answers ?? []);
        setServerUserAnswerIds(answers);
    }, [testingQuestion]);

    const commitSendAnswer = async (): Promise<void> => {
        try {
            await sendAnswer({
                variables: {
                    courseId: test.courseId,
                    subjectId: test.subjectId,
                    answer: {
                        questionId: testingQuestion.id,
                        answerIds,
                    },
                },
            });
        } catch {
            enqueueError(t('testing:notifiers.send_answer.error'));
        }
    };

    const hasServerUserAnswers = serverUserAnswerIds !== null;
    const questionsLength = test.questionIds.length;

    const commitChangeQuestionIndex = (type: 'increment' | 'decrement' | 'stop'): void => {
        if (questionsLength === 1 || type === 'stop') {
            onAnswer();
            return;
        }

        let updatedIndex =
            type === 'increment' ? currentQuestionIndex + 1 : currentQuestionIndex - 1;

        const isLastQuestion = updatedIndex > questionsLength;
        const isFirstQuestion = updatedIndex === 0;

        if (isLastQuestion) {
            updatedIndex = 1;
        }

        if (isFirstQuestion) {
            updatedIndex = questionsLength;
        }

        setQuery({ ...query, question: updatedIndex });
    };

    const handleClickSubmitAnswer = useEventAsync(async (): Promise<void> => {
        const isLastQuestion = currentQuestionIndex === questionsLength;

        await commitSendAnswer();

        commitChangeQuestionIndex(isLastQuestion ? 'stop' : 'increment');
    });

    const handleClickFinishTest = useEventAsync(async (): Promise<void> => {
        commitSendAnswer();
        onFinish();
    });

    const renderedAnswersActions = React.useMemo(() => {
        if (hasServerUserAnswers) {
            return <Button disabled>{t('testing:actions.already_answered')}</Button>;
        }

        return (
            <Button loading={sendAnswerLoading} onClick={handleClickSubmitAnswer}>
                {t('testing:actions.send_answer')}
            </Button>
        );
    }, [
        t,
        sendAnswerLoading,
        hasServerUserAnswers,
        handleClickSubmitAnswer,
        handleClickFinishTest,
    ]);

    if (testingQuestionLoading) {
        return <Loader />;
    }

    const hasMultipleCorrectAnswers = testingQuestion.hasMultipleCorrectAnswers();
    const withoutAnswerQuestionsIndex = test.questionIds
        .map((questionId, index) => {
            const hasAnswers = test.answers.find(answer => answer.questionId === questionId);

            if (!hasAnswers) {
                return index + 1;
            }

            return null;
        })
        .filter(Boolean) as number[];

    const handleChangeQuestion = (type: 'increment' | 'decrement') => (): void => {
        commitChangeQuestionIndex(type);
    };

    const handleChangeAnswer =
        (answerId: string) =>
        (_: React.ChangeEvent<{}>): void => {
            if (!hasMultipleCorrectAnswers) {
                setAnswerIds([answerId]);
                return;
            }

            setAnswerIds(state => {
                const idx = state.findIndex(answer => answer === answerId);

                if (idx > -1) {
                    return ArrayHelper.remove(state, idx);
                }

                return ArrayHelper.add(state, answerId);
            });
        };

    const handleOnTimeUp = async (): Promise<void> => {
        onTimeUp();
    };

    return (
        <Root>
            <Header>
                <TestingNavigation
                    currentIndex={currentQuestionIndex}
                    questionsLength={questionsLength}
                    onPreviousClick={handleChangeQuestion('decrement')}
                    onNextClick={handleChangeQuestion('increment')}
                />

                <TestingTimer until={test.leftTime} onTimeUp={handleOnTimeUp} />

                <FinishTestButton
                    withoutAnswerQuestionsIndex={withoutAnswerQuestionsIndex}
                    onFinish={handleClickFinishTest}
                />
            </Header>

            <TestingQuestion text={testingQuestion.text} src={testingQuestion.photos[0]?.origin} />

            <TestingAnswers
                title={
                    hasMultipleCorrectAnswers
                        ? t('testing:actions.choose_multiple_answers')
                        : t('testing:actions.choose_single_answer')
                }
                disabled={hasServerUserAnswers}
                answers={testingQuestion.answers}
                answerIds={answerIds}
                onChange={handleChangeAnswer}
                hasMultipleCorrectAnswers={hasMultipleCorrectAnswers}
                actions={renderedAnswersActions}
            />
        </Root>
    );
};

export { TestingForm };
